import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Twitter, Instagram, Mail, Phone, MapPin } from 'lucide-react';

const Footer: React.FC = () => {
  const [logoError, setLogoError] = useState(false);
  const [brandLogoError, setBrandLogoError] = useState(false);

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-4">
            <div className="flex items-center gap-0.5">
              {logoError ? (
                <div className="w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg">C9</span>
                </div>
              ) : (
                <img 
                  src={process.env.PUBLIC_URL + '/brand-logo.png'}
                  alt="c nine logo" 
                  className="h-10 md:h-12 w-auto object-contain max-w-[50px] md:max-w-[60px]"
                  style={{ imageRendering: 'crisp-edges', marginRight: '10px'}}
                  onError={() => setLogoError(true)}
                />
              )}
              {brandLogoError ? (
                <span className="text-xl font-bold">c nine</span>
              ) : (
                <img 
                  src={process.env.PUBLIC_URL + '/brand-logo-bg.png'}
                  alt="c nine brand" 
                  className="h-8 md:h-10 w-auto object-contain max-w-[180px] md:max-w-[220px]"
                  style={{ imageRendering: 'crisp-edges' }}
                  onError={() => setBrandLogoError(true)}
                />
              )}
            </div>
            <p className="text-gray-300 text-sm leading-relaxed">
              Your trusted partner for premium tech accessories. We bring you the latest in innovation 
              with quality products that enhance your digital lifestyle.
            </p>
            <div className="flex space-x-4">
              <a href="https://www.facebook.com/people/CNine-India/61587578342562" target="_blank" rel="noreferrer noopener" className="text-gray-400 hover:text-white transition-colors">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="https://twitter.com" target="_blank" rel="noreferrer noopener" className="text-gray-400 hover:text-white transition-colors">
                <Twitter className="w-5 h-5" />
              </a>
              <a href="https://www.instagram.com/cnineind" target="_blank" rel="noreferrer noopener" className="text-gray-400 hover:text-white transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-gray-300 hover:text-white transition-colors text-sm">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/products" className="text-gray-300 hover:text-white transition-colors text-sm">
                  All Products
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-gray-300 hover:text-white transition-colors text-sm">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-300 hover:text-white transition-colors text-sm">
                  Contact
                </Link>
              </li>
              <li>
                <Link to="/support" className="text-gray-300 hover:text-white transition-colors text-sm">
                  Support
                </Link>
              </li>
              <li>
                <Link to="/admin/login" className="text-gray-300 hover:text-white transition-colors text-sm">
                  Admin Login
                </Link>
              </li>
            </ul>
          </div>

          {/* Categories */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Categories</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/products?category=speakers" className="text-gray-300 hover:text-white transition-colors text-sm">
                  Speakers
                </Link>
              </li>
              <li>
                <Link to="/products?category=chargers" className="text-gray-300 hover:text-white transition-colors text-sm">
                  Chargers
                </Link>
              </li>
              <li>
                <Link to="/products?category=headphones" className="text-gray-300 hover:text-white transition-colors text-sm">
                  Headphones
                </Link>
              </li>
              <li>
                <Link to="/products?category=accessories" className="text-gray-300 hover:text-white transition-colors text-sm">
                  Accessories
                </Link>
              </li>
              <li>
                <Link to="/products?category=powerbanks" className="text-gray-300 hover:text-white transition-colors text-sm">
                  Power Banks
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Contact Info</h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <MapPin className="w-4 h-4 text-primary-500" />
                <span className="text-gray-300 text-sm">
                JSB Future Group<br />                  
                </span>
              </div>              
              <div className="flex items-center space-x-3">
                <Mail className="w-4 h-4 text-primary-500" />
                <span className="text-gray-300 text-sm">info@cnineindia.com</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-gray-400 text-sm">
              All Rights Cnine India 2026 | Designed By <a href="https://techinvergesolutions.com" target="_blank" rel="noopener noreferrer" className="text-white hover:text-primary-500 transition-colors">Tech Inverge Solutions</a>
            </div>
            <div className="flex space-x-6 text-sm">
              <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-gray-400 hover:text-white transition-colors">
                Terms of Service
              </Link>
              <Link to="/refunds" className="text-gray-400 hover:text-white transition-colors">
                Refund Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
